import { DOH_PHASE } from '../entities/doh.js';
import { FIELD } from '../core/config.js';
import { drawText } from './font.js';
import { UI } from './palette.js';

/**
 * DOH is drawn as a carved stone head: flat slabs, hard bevels, and cracks
 * that spread across the face as its health drops.
 */

const STONE = '#6f6558';
const STONE_LIGHT = '#9a9083';
const STONE_DARK = '#443f37';
const STONE_EDGE = '#2a2621';
const SOCKET = '#181512';
const EYE = '#e04a3c';

export function drawDoh(ctx, doh) {
  const x = Math.round(doh.cx - doh.w / 2);
  const y = Math.round(doh.y);
  const w = doh.w;
  const h = doh.h;

  if (doh.phase === DOH_PHASE.DYING) {
    // Shudder violently while breaking apart.
    ctx.save();
    ctx.translate((Math.random() - 0.5) * 6, (Math.random() - 0.5) * 6);
  }

  ctx.fillStyle = STONE_EDGE;
  ctx.fillRect(x - 2, y - 2, w + 4, h + 4);

  // Side slabs (the shoulders of the moai).
  ctx.fillStyle = STONE_DARK;
  ctx.fillRect(x - 6, y + 26, 8, h - 44);
  ctx.fillRect(x + w - 2, y + 26, 8, h - 44);

  ctx.fillStyle = STONE;
  ctx.fillRect(x, y, w, h);

  ctx.fillStyle = STONE_LIGHT;
  ctx.fillRect(x, y, w, 3);
  ctx.fillRect(x, y, 3, h);
  ctx.fillStyle = STONE_DARK;
  ctx.fillRect(x, y + h - 4, w, 4);
  ctx.fillRect(x + w - 4, y, 4, h);

  drawBrow(ctx, x, y, w);
  drawEyes(ctx, doh, x, y, w);
  drawNose(ctx, x, y, w, h);
  drawMouth(ctx, doh, x, y, w, h);
  drawCracks(ctx, doh, x, y, w, h);

  if (doh.hitFlash > 0) {
    ctx.fillStyle = `rgba(255,235,225,${(doh.hitFlash * 0.6).toFixed(3)})`;
    ctx.fillRect(x - 2, y - 2, w + 4, h + 4);
  }

  if (doh.phase === DOH_PHASE.DYING) ctx.restore();
}

function drawBrow(ctx, x, y, w) {
  // A heavy overhanging brow is what makes a moai read as a moai.
  ctx.fillStyle = STONE_LIGHT;
  ctx.fillRect(x + 4, y + 16, w - 8, 8);
  ctx.fillStyle = STONE_DARK;
  ctx.fillRect(x + 4, y + 24, w - 8, 6);
  ctx.fillStyle = STONE_EDGE;
  ctx.fillRect(x + 4, y + 30, w - 8, 3);
}

function drawEyes(ctx, doh, x, y, w) {
  const eyeY = y + 33;
  const rage = 1 - doh.hp / doh.maxHp;
  const pupil = doh.phase === DOH_PHASE.DYING ? '#ffd25e' : EYE;

  for (const side of [0, 1]) {
    const ex = side === 0 ? x + 12 : x + w - 34;

    ctx.fillStyle = SOCKET;
    ctx.fillRect(ex, eyeY, 22, 16);
    // Shadow cast by the brow, deepest at the top of the socket.
    ctx.fillStyle = '#0d0b09';
    ctx.fillRect(ex, eyeY, 22, 4);

    ctx.fillStyle = pupil;
    const pw = 9 + Math.round(rage * 4);
    ctx.fillRect(ex + 11 - Math.floor(pw / 2), eyeY + 6, pw, 7);
    ctx.fillStyle = '#ffd9d2';
    ctx.fillRect(ex + 11 - Math.floor(pw / 2) + 1, eyeY + 7, 2, 2);
  }
}

function drawNose(ctx, x, y, w, h) {
  const cx = x + w / 2;

  ctx.fillStyle = STONE_LIGHT;
  ctx.fillRect(cx - 5, y + 30, 10, 28);
  ctx.fillRect(cx - 9, y + 54, 18, 11);

  ctx.fillStyle = STONE_DARK;
  ctx.fillRect(cx + 2, y + 30, 3, 28);
  ctx.fillRect(cx + 5, y + 54, 4, 11);

  ctx.fillStyle = SOCKET;
  ctx.fillRect(cx - 7, y + 59, 3, 4);
  ctx.fillRect(cx + 4, y + 59, 3, 4);

  // Cheek grooves and a jaw line finish the carved look.
  ctx.fillStyle = STONE_DARK;
  ctx.fillRect(x + 9, y + 54, 2, 26);
  ctx.fillRect(x + w - 11, y + 54, 2, 26);
  ctx.fillRect(x + 8, y + h - 11, w - 16, 3);
}

function drawMouth(ctx, doh, x, y, w, h) {
  const openH = 5 + Math.round(doh.mouth * 15);
  const mx = x + 22;
  const mw = w - 44;
  const my = y + h - 28;

  ctx.fillStyle = STONE_EDGE;
  ctx.fillRect(mx - 3, my - 3, mw + 6, openH + 5);
  ctx.fillStyle = SOCKET;
  ctx.fillRect(mx, my, mw, openH);

  if (doh.mouth > 0.35) {
    // The next orb charging in the throat.
    ctx.fillStyle = '#c14fd8';
    const orb = Math.round(doh.mouth * 5);
    ctx.fillRect(x + w / 2 - orb, my + openH / 2 - orb / 2, orb * 2, Math.max(2, orb));
  }

  ctx.fillStyle = STONE_LIGHT;
  for (let i = 0; i < 4; i += 1) ctx.fillRect(mx + 3 + i * 12, my, 3, 3);
}

function drawCracks(ctx, doh, x, y, w, h) {
  const damage = 1 - doh.hp / doh.maxHp;
  if (damage < 0.25) return;

  ctx.strokeStyle = STONE_EDGE;
  ctx.lineWidth = 2;
  // Cracks start at an edge and travel inward, the way stone actually fails.
  ctx.beginPath();
  ctx.moveTo(x, y + 9);
  ctx.lineTo(x + 13, y + 15);
  ctx.lineTo(x + 10, y + 22);
  if (damage > 0.45) {
    ctx.moveTo(x + w, y + 58);
    ctx.lineTo(x + w - 14, y + 64);
    ctx.lineTo(x + w - 11, y + 74);
  }
  if (damage > 0.7) {
    ctx.moveTo(x + 26, y + h);
    ctx.lineTo(x + 32, y + h - 14);
    ctx.moveTo(x + w - 30, y);
    ctx.lineTo(x + w - 34, y + 13);
  }
  ctx.stroke();
}

/** Boss health bar, pinned just under the top frame. */
export function drawDohHealth(ctx, doh) {
  const w = FIELD.width - 40;
  const x = FIELD.left + 20;
  const y = FIELD.top + 8;
  const ratio = Math.max(0, doh.hp / doh.maxHp);

  drawText(ctx, 'DOH', x, y - 1, { scale: 1, color: UI.inkDim });

  ctx.fillStyle = '#1b1f26';
  ctx.fillRect(x + 26, y, w - 26, 7);
  ctx.fillStyle = ratio > 0.35 ? UI.accent : UI.danger;
  ctx.fillRect(x + 27, y + 1, Math.round((w - 28) * ratio), 5);
  ctx.fillStyle = '#000000';
  for (let i = 1; i < 6; i += 1) {
    ctx.fillRect(x + 26 + Math.round(((w - 26) * i) / 6), y, 1, 7);
  }
}

